import { useEffect, useMemo, useState } from 'react'
import {
  Bell,
  BookOpen,
  FileBarChart,
  Images,
  KeyRound,
  Mails,
  MessagesSquare,
  Palette,
  Type,
  Video,
} from 'lucide-react'
import Button from '../../components/Button'
import DataTable from '../../components/DataTable'
import FileField from '../../components/FileField'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import PromptDialog from '../../components/PromptDialog'
import Select from '../../components/Select'
import SectionNav from '../../components/SectionNav'
import useHashTab from '../../components/useHashTab'
import BrandThemeCard from './BrandThemeCard'
import TextField from '../../components/TextField'
import SettingsCard, { Field, ToggleRow } from './SettingsCard'
import { useToast } from '../../components/toastContext'
import { getSettings, parseChannel, saveSection, testEmailSettings } from './settingsService'
import { common as C, settingsKeysStrings as K, settingsStrings as S } from './strings'
import './settings.css'
import { serverText } from '../../lib/i18n'

/**
 * The same seven sections, clustered by subject rather than run together as one
 * undifferentiated line. `بيانات الإعدادات` joins the strip as the last entry: it
 * is a settings *view*, not a separate destination — but it keeps its own route
 * and its own guard, so nothing about reaching it changes.
 *
 * The clusters are unlabelled: a hairline separates them, so no wording is invented.
 */
const SECTION_GROUPS = [
  {
    items: [
      { id: 'identity', label: S.theme.title, icon: Palette },
      { id: 'media', label: S.tabs.media, icon: Images },
      { id: 'keys', label: S.tabs.keys, icon: Type },
      { id: 'contact', label: S.tabs.contact, icon: Mails },
    ],
  },
  {
    items: [
      { id: 'video', label: S.tabs.video, icon: Video },
      { id: 'userManual', label: S.tabs.userManual, icon: BookOpen },
    ],
  },
  {
    items: [
      { id: 'reports', label: S.tabs.reports, icon: FileBarChart },
      { id: 'notifications', label: S.tabs.notifications, icon: Bell },
      { id: 'email', label: S.tabs.email, icon: Mails },
      { id: 'messaging', label: S.tabs.messaging, icon: MessagesSquare },
    ],
  },
  { items: [{ id: 'settingsKeys', label: K.title, to: '/settings/keys', icon: KeyRound }] },
]

/* Every panel this page answers to in its URL hash. The entry carrying its own
   `to` is a link out of the page, not a panel of it. */
const SECTION_IDS = SECTION_GROUPS.flatMap((group) =>
  group.items.filter((item) => !item.to).map((item) => item.id),
)

const SHOW_HIDE = [
  { value: 'true', label: C.show },
  { value: 'false', label: C.hide },
]
const YES_NO = [
  { value: 'false', label: C.no },
  { value: 'true', label: C.yes },
]
const THEMES = [
  { value: 'white', label: S.media.white },
  { value: 'black', label: S.media.black },
]

const asDate = (iso) => (iso ? iso.replace('T', ' ') : '')

/**
 * The three notification channels. Each is one settings row holding a JSON blob, and each
 * has its own save action, so they differ only in their field list.
 */
const CHANNELS = [
  {
    id: 'whatsapp',
    enabledKey: 'whatsappEnabled',
    fields: [
      { key: 'whatsappApiUrl', label: 'apiUrl' },
      { key: 'whatsappApiKey', label: 'apiKey' },
      { key: 'whatsappSender', label: 'senderNumber' },
    ],
  },
  {
    id: 'slack',
    enabledKey: 'slackEnabled',
    fields: [
      { key: 'slackWebhook', label: 'webhookUrl' },
      { key: 'slackBotToken', label: 'botToken' },
      { key: 'slackChannel', label: 'channel' },
    ],
  },
  {
    id: 'sms',
    enabledKey: 'smsEnabled',
    fields: [
      { key: 'smsApiUrl', label: 'apiUrl' },
      { key: 'smsApiKey', label: 'apiKey' },
      { key: 'smsSenderId', label: 'senderId' },
    ],
  },
]

/**
 * The value the legacy form starts with — the stored setting, or the empty string when
 * the key has never been written. Booleans stay strings because the selects are
 * `<option value="true">`.
 */
const pick = (settings, key, fallback = '') => settings?.[key] ?? fallback

/** إعدادات النظام — Views/Settings/Index.cshtml. */
export default function SettingsPage() {
  const toast = useToast()
  const [tab, setTab] = useHashTab('media', SECTION_IDS)
  const [data, setData] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState('')
  const [testing, setTesting] = useState(false)
  const [askingTest, setAskingTest] = useState(false)

  useEffect(() => {
    getSettings().then((response) => {
      setData(response)
      setForm(initialForm(response))
    })
  }, [])

  const set = (key) => (value) => setForm((previous) => ({ ...previous, [key]: value }))

  const toggle = (key) => (checked) => setForm((previous) => ({ ...previous, [key]: checked }))

  /**
   * Each card posts on its own, so the pending state is keyed by section.
   *
   * The outcome is always reported: these settings change what every other screen does,
   * and a save that quietly failed would be discovered much later and somewhere else.
   */
  const save = (section) => async () => {
    setSaving(section)
    const outcome = await saveSection(section, form)
    setSaving('')

    if (outcome.ok) {
      toast.success(serverText(outcome.message) || C.saved)
      return
    }

    // Nothing was chosen, or the card cannot post what it has - not a failure to report.
    if (outcome.nothingToSave) return

    if (outcome.unsupported) {
      toast.error(C.notAvailable)
      return
    }

    toast.error(outcome.message || C.saveFailed)
  }

  /**
   * The probe. It asks WHERE to send first — a test that silently mails the sender address
   * proves the server accepts the credentials but tells nobody whether the message arrives,
   * and the address to try is rarely the sender's own.
   *
   * Reports either way; a silent test button tells you nothing.
   */
  const runEmailTest = async (address) => {
    setTesting(true)
    const outcome = await testEmailSettings(form, address)
    setTesting(false)
    setAskingTest(false)
    if (outcome.ok) toast.success(serverText(outcome.message) || C.saved)
    else toast.error(outcome.message || C.saveFailed)
  }

  const reset = (section) => () =>
    setForm((previous) => ({ ...previous, ...sectionDefaults(section, data) }))

  const templateColumns = useMemo(
    () => [
      {
        key: 'index',
        header: S.notifications.index,
        width: '64px',
        searchable: false,
        /* The legacy numbers the rows server-side, so sorting on this column restores
           the order the templates were loaded in — which is by event type. */
        value: (row) => row.type,
        render: (_row, index) => <span className="set-index">{index + 1}</span>,
      },
      {
        key: 'name',
        header: S.notifications.name,
        render: (row) => <bdi>{row.name}</bdi>,
      },
      {
        key: 'lastUpdated',
        header: S.notifications.lastUpdated,
        render: (row) => (
          <span className="set-date" dir="ltr">
            {asDate(row.lastUpdated)}
          </span>
        ),
      },
      /* NO ACTIONS COLUMN. The pencil and the auto-days button had no handler, and
         `/Settings/SaveEmailTemplate` has no caller anywhere — so the whole tab is
         read-only and now says so by not offering two controls that open nothing. The
         editor is a real piece of work: a template is HTML with substitution tokens.
         COSMETIC_UI.md. */
    ],
    [],
  )

  /** Attribute1..5 wear the name configured under إعدادات متقدمة. */
  const attributeLabel = (attribute) =>
    attribute.label ?? pick(data?.settings, attribute.setting, attribute.name)

  const toggleColumn = (key, name) =>
    setForm((previous) => {
      const current = previous[key] ?? []
      return {
        ...previous,
        [key]: current.includes(name)
          ? current.filter((item) => item !== name)
          : [...current, name],
      }
    })

  // Every field is controlled, so the form cannot render before its values exist —
  // React warns the moment an input flips from undefined to a value.
  if (!data) {
    return (
      <>
        <PageHeader title={S.title} />
        <PageLoader />
      </>
    )
  }

  return (
    <>
      <PageHeader title={S.title} />

      <div className="set-workspace">
        <SectionNav
          groups={SECTION_GROUPS}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        <div className="set-panel">
      {tab === 'identity' && <BrandThemeCard initialBrand={data.brand} />}

      {tab === 'media' && (
        <div className="set-grid">
          <SettingsCard
            title={S.media.whiteLogo}
            hint={S.media.whiteLogoHint}
            spec={S.media.whiteLogoSpec}
            onSave={save('logo')}
            onReset={reset('logo')}
            saving={saving === 'logo'}
          >
            <FileField id="logo" accept=".png" onChange={set('logo')} />
            <Field label={S.media.logoBackground}>
              {(id) => (
                <Select
                  id={id}
                  value={form.logoBackground}
                  onChange={set('logoBackground')}
                  options={SHOW_HIDE}
                />
              )}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.media.mainLogo}
            hint={S.media.mainLogoHint}
            spec={S.media.whiteLogoSpec}
            onSave={save('reportLogo')}
            onReset={reset('reportLogo')}
            saving={saving === 'reportLogo'}
          >
            <FileField id="reportlogo" accept=".png" onChange={set('reportLogo')} />
          </SettingsCard>

          <SettingsCard
            title={S.media.sideImage}
            spec={S.media.sideImageSpec}
            onSave={save('sideLogin')}
            onReset={reset('sideLogin')}
            saving={saving === 'sideLogin'}
          >
            <Field label={S.media.arabicImage}>
              {(id) => <FileField id={id} accept=".png" onChange={set('sideLoginAr')} />}
            </Field>
            <Field label={S.media.englishImage}>
              {(id) => <FileField id={id} accept=".png" onChange={set('sideLoginEn')} />}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.media.background}
            spec={S.media.backgroundSpec}
            onSave={save('backgroundLogin')}
            onReset={reset('backgroundLogin')}
            saving={saving === 'backgroundLogin'}
          >
            <Field label={S.media.imageAr}>
              {(id) => <FileField id={id} accept=".jpg" onChange={set('backgroundAr')} />}
            </Field>
            <Field label={S.media.imageEn}>
              {(id) => <FileField id={id} accept=".jpg" onChange={set('backgroundEn')} />}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.media.loginAndLogo}
            onSave={save('loginTheme')}
            onReset={reset('loginTheme')}
            saving={saving === 'loginTheme'}
          >
            <Field label={S.media.loginTheme}>
              {(id) => (
                <Select id={id} value={form.theme} onChange={set('theme')} options={THEMES} />
              )}
            </Field>
            <Field label={S.media.logoOnLogin}>
              {(id) => (
                <Select
                  id={id}
                  value={form.logoOnLogin}
                  onChange={set('logoOnLogin')}
                  options={SHOW_HIDE}
                />
              )}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.media.favicon}
            spec={S.media.faviconSpec}
            onSave={save('favicon')}
            onReset={reset('favicon')}
            saving={saving === 'favicon'}
          >
            {/* `.ico` as well as `.png`: the endpoint writes whatever it is given to
                `favicon.ico`, so refusing an actual icon file was the wrong way round. */}
            <FileField id="favico" accept=".ico,.png" onChange={set('favicon')} />
          </SettingsCard>
        </div>
      )}

      {tab === 'keys' && (
        <div className="set-grid">
          <SettingsCard
            title={S.keys.appTitle}
            onSave={save('appTitle')}
            onReset={reset('appTitle')}
            saving={saving === 'appTitle'}
          >
            <Field label={S.keys.arabicAppTitle}>
              {(id) => (
                <TextField id={id} value={form.arabicAppTitle} onChange={set('arabicAppTitle')} />
              )}
            </Field>
            <Field label={S.keys.englishAppTitle}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.englishAppTitle}
                  onChange={set('englishAppTitle')}
                  dir="ltr"
                />
              )}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.keys.footer}
            onSave={save('footer')}
            onReset={reset('footer')}
            saving={saving === 'footer'}
          >
            <Field label={S.keys.footerAr}>
              {(id) => <TextField id={id} value={form.footer} onChange={set('footer')} />}
            </Field>
            <Field label={S.keys.footerEn}>
              {(id) => <TextField id={id} value={form.footerEn} onChange={set('footerEn')} />}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.keys.organization}
            onSave={save('organization')}
            onReset={reset('organization')}
            saving={saving === 'organization'}
          >
            <Field label={S.keys.arabicOrgName}>
              {(id) => (
                <TextField id={id} value={form.arabicOrgName} onChange={set('arabicOrgName')} />
              )}
            </Field>
            <Field label={S.keys.englishOrgName}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.englishOrgName}
                  onChange={set('englishOrgName')}
                  dir="ltr"
                />
              )}
            </Field>
            <Field label={S.keys.orgEmail} hint={S.keys.orgEmailHint}>
              {(id) => (
                <TextField id={id} value={form.orgEmail} onChange={set('orgEmail')} dir="ltr" />
              )}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.keys.domain}
            spec={S.keys.domainSpec}
            onSave={save('domain')}
            onReset={reset('domain')}
            saving={saving === 'domain'}
          >
            <Field label={S.keys.domain} note={S.keys.domainNote}>
              {(id) => (
                <TextField id={id} value={form.domainUrl} onChange={set('domainUrl')} dir="ltr" />
              )}
            </Field>
            <Field label={S.keys.useHsts}>
              {(id) => (
                <Select id={id} value={form.useHsts} onChange={set('useHsts')} options={YES_NO} />
              )}
            </Field>
          </SettingsCard>

          {/* Beside the domain because it is the same kind of thing: where this
              installation lives, as the rest of the platform addresses it. */}
          <SettingsCard
            title={S.servicesUrl.title}
            onSave={save('servicesUrl')}
            onReset={reset('servicesUrl')}
            saving={saving === 'servicesUrl'}
          >
            <Field label={S.servicesUrl.label}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.servicesUrl}
                  onChange={set('servicesUrl')}
                  dir="ltr"
                />
              )}
            </Field>
          </SettingsCard>
        </div>
      )}

      {tab === 'contact' && (
        <SettingsCard
          title={S.contact.title}
          onSave={save('contact')}
          onReset={reset('contact')}
          saving={saving === 'contact'}
        >
          {!form.enableEmail && !form.showPhone && (
            <p className="set-alert" role="alert">
              <bdi>{S.contact.allDisabledWarning}</bdi>
            </p>
          )}

          <ToggleRow
            id="chkBxEnableContactPage"
            checked={!!form.enableContactPage}
            onChange={toggle('enableContactPage')}
            label={S.contact.enablePage}
          />

          <fieldset className="set-fieldset" disabled={!form.enableContactPage}>
            <Field label={S.contact.pageTitle}>
              <div className="set-pair">
                <TextField
                  value={form.contactPageTitle}
                  onChange={set('contactPageTitle')}
                  placeholder={C.arabic}
                />
                <TextField
                  value={form.contactPageTitleEn}
                  onChange={set('contactPageTitleEn')}
                  placeholder={C.english}
                  dir="ltr"
                />
              </div>
            </Field>

            <Field label={S.contact.pageHeader}>
              <div className="set-pair">
                <TextField
                  value={form.contactPageHeader}
                  onChange={set('contactPageHeader')}
                  placeholder={C.arabic}
                />
                <TextField
                  value={form.contactPageHeaderEn}
                  onChange={set('contactPageHeaderEn')}
                  placeholder={C.english}
                  dir="ltr"
                />
              </div>
            </Field>

            <div className="set-subpanel">
              <ToggleRow
                id="chkBxEnableEmail"
                checked={!!form.enableEmail}
                onChange={toggle('enableEmail')}
                label={S.contact.enableEmail}
              />
              {/* updateAreaState dims and disables an area whose toggle is off. */}
              <fieldset className="set-subpanel__body" disabled={!form.enableEmail}>
                <Field label={S.contact.emailAddress}>
                  {(id) => (
                    <TextField
                      id={id}
                      type="email"
                      value={form.contactEmail}
                      onChange={set('contactEmail')}
                      dir="ltr"
                    />
                  )}
                </Field>
                <Field label={S.contact.emailOptions}>
                  <div className="set-checks">
                    <ToggleRow
                      id="chkBxShowSendEmailForm"
                      checked={!!form.showSendForm}
                      onChange={toggle('showSendForm')}
                      label={S.contact.showSendForm}
                    />
                    <ToggleRow
                      id="chkBxShowEmailInfo"
                      checked={!!form.showEmailInfo}
                      onChange={toggle('showEmailInfo')}
                      label={S.contact.showEmailInfo}
                    />
                  </div>
                </Field>
                <Field label={S.contact.emailLabel}>
                  <div className="set-pair">
                    <TextField
                      value={form.contactEmailLabel}
                      onChange={set('contactEmailLabel')}
                      placeholder={C.arabic}
                    />
                    <TextField
                      value={form.contactEmailEnLabel}
                      onChange={set('contactEmailEnLabel')}
                      placeholder={C.english}
                      dir="ltr"
                    />
                  </div>
                </Field>
              </fieldset>
            </div>

            <div className="set-subpanel">
              <ToggleRow
                id="ChkBxShowPhone"
                checked={!!form.showPhone}
                onChange={toggle('showPhone')}
                label={S.contact.showPhone}
              />
              <fieldset className="set-subpanel__body" disabled={!form.showPhone}>
                <Field label={S.contact.phone}>
                  {(id) => (
                    <TextField
                      id={id}
                      value={form.contactPhone}
                      onChange={set('contactPhone')}
                      dir="ltr"
                    />
                  )}
                </Field>
                <Field label={S.contact.phoneLabel}>
                  <div className="set-pair">
                    <TextField
                      value={form.contactPhoneLabel}
                      onChange={set('contactPhoneLabel')}
                      placeholder={C.arabic}
                    />
                    <TextField
                      value={form.contactPhoneEnLabel}
                      onChange={set('contactPhoneEnLabel')}
                      placeholder={C.english}
                      dir="ltr"
                    />
                  </div>
                </Field>
              </fieldset>
            </div>
          </fieldset>
        </SettingsCard>
      )}

      {tab === 'video' && (
        <SettingsCard
          title={S.video.title}
          onSave={save('video')}
          onReset={reset('video')}
          saving={saving === 'video'}
        >
          <ToggleRow
            id="ProgressBarEnabled"
            checked={!!form.progressBar}
            onChange={toggle('progressBar')}
            label={S.video.progressBar}
            hint={S.video.progressBarHint}
          />
          <ToggleRow
            id="ForceSubtitleEnabled"
            checked={!!form.forceSubtitle}
            onChange={toggle('forceSubtitle')}
            label={S.video.forceSubtitle}
            hint={S.video.forceSubtitleHint}
          />
          <ToggleRow
            id="enableUserSubtitleControl"
            checked={!!form.userSubtitleControl}
            onChange={toggle('userSubtitleControl')}
            label={S.video.userSubtitleControl}
            hint={S.video.userSubtitleControl}
          />
          <ToggleRow
            id="inForceVideoPauseOnBlur"
            checked={!!form.pauseOnBlur}
            onChange={toggle('pauseOnBlur')}
            label={S.video.pauseOnBlur}
            hint={S.video.pauseOnBlurHint}
          />
          <ToggleRow
            id="inEnablePlayerPlaybackRates"
            checked={!!form.playbackRates}
            onChange={toggle('playbackRates')}
            label={S.video.playbackRates}
            hint={S.video.playbackRatesHint}
          />
        </SettingsCard>
      )}

      {tab === 'userManual' && (
        <div className="set-grid">
          <SettingsCard
            title={S.userManual.guide}
            onSave={save('userGuide')}
            onReset={reset('userGuide')}
            saving={saving === 'userGuide'}
          >
            <Field label={S.userManual.guideAr}>
              {(id) => (
                <FileField
                  id={id}
                  accept={form.introVideo ? '.mp4' : '.pdf'}
                  onChange={set('guideAr')}
                />
              )}
            </Field>
            <Field label={S.userManual.guideEn}>
              {(id) => (
                <FileField
                  id={id}
                  accept={form.introVideo ? '.mp4' : '.pdf'}
                  onChange={set('guideEn')}
                />
              )}
            </Field>
          </SettingsCard>

          <SettingsCard
            title={S.userManual.settings}
            onSave={save('userManual')}
            onReset={reset('userManual')}
            saving={saving === 'userManual'}
          >
            <ToggleRow
              id="Default_EnableUserManualPopup"
              checked={!!form.manualPopup}
              onChange={toggle('manualPopup')}
              label={S.userManual.popup}
            />
            <ToggleRow
              id="EnableIntroVideo"
              checked={!!form.introVideo}
              onChange={toggle('introVideo')}
              label={S.userManual.introVideo}
            />
            <ToggleRow
              id="UserGuideLinkOnTheLoginPage"
              checked={!!form.guideLinkOnLogin}
              onChange={toggle('guideLinkOnLogin')}
              label={S.userManual.linkOnLogin}
            />
            <ToggleRow
              id="ShowUserGuideOnSeparatePage"
              checked={!!form.guideSeparatePage}
              onChange={toggle('guideSeparatePage')}
              label={S.userManual.separatePage}
            />
          </SettingsCard>

          <SettingsCard
            title={S.userManual.adminUrl}
            onSave={save('adminGuide')}
            onReset={reset('adminGuide')}
            saving={saving === 'adminGuide'}
          >
            <Field label={S.userManual.adminUrl}>
              {(id) => (
                <TextField
                  id={id}
                  value={form.adminManualUrl}
                  onChange={set('adminManualUrl')}
                  placeholder={S.userManual.adminUrl}
                  dir="ltr"
                />
              )}
            </Field>
          </SettingsCard>
        </div>
      )}

      {tab === 'reports' && (
        <SettingsCard
          title={S.reports.title}
          onSave={save('reports')}
          onReset={reset('reports')}
          saving={saving === 'reports'}
        >
          <ToggleRow
            id="chkBxEnableUserStatistics"
            checked={!!form.userStatistics}
            onChange={toggle('userStatistics')}
            label={S.reports.userStatistics}
          />

          <div className="set-columns">
            {[
              { key: 'policiesColumns', label: S.reports.policiesColumns },
              { key: 'examsColumns', label: S.reports.examsColumns },
            ].map(({ key, label }) => (
              <div key={key} className="set-columns__group">
                <h4 className="set-columns__title">
                  <bdi>{label}</bdi>
                </h4>
                {S.profileAttributes.map((attribute) => (
                  <ToggleRow
                    key={`${key}-${attribute.name}`}
                    id={`${key}-${attribute.name}`}
                    checked={(form[key] ?? []).includes(attribute.name)}
                    onChange={() => toggleColumn(key, attribute.name)}
                    label={attributeLabel(attribute)}
                  />
                ))}
              </div>
            ))}
          </div>
        </SettingsCard>
      )}

      {tab === 'email' && (
        <div className="set-grid">
          <SettingsCard
            title={S.email.title}
            onSave={save('email')}
            onReset={reset('email')}
            saving={saving === 'email'}
            actions={
              <Button variant="ghost" size="sm" onClick={() => setAskingTest(true)} disabled={testing}>
                {testing ? '…' : S.email.test}
              </Button>
            }
          >
            <Field label={S.email.host}>
              {(id) => <TextField id={id} value={form.emailHost} onChange={set('emailHost')} />}
            </Field>
            <Field label={S.email.port} hint={S.email.portHint}>
              {(id) => <TextField id={id} value={form.emailPort} onChange={set('emailPort')} />}
            </Field>
            <Field label={S.email.from} hint={S.email.fromHint}>
              {(id) => <TextField id={id} value={form.emailFrom} onChange={set('emailFrom')} />}
            </Field>
            <Field label={S.email.senderName}>
              {(id) => (
                <TextField id={id} value={form.emailSenderName} onChange={set('emailSenderName')} />
              )}
            </Field>
            <Field label={S.email.username}>
              {(id) => (
                <TextField id={id} value={form.emailUsername} onChange={set('emailUsername')} />
              )}
            </Field>
            <Field label={S.email.password}>
              {(id) => (
                <TextField
                  id={id}
                  type="password"
                  value={form.emailPassword}
                  onChange={set('emailPassword')}
                />
              )}
            </Field>
            <Field label={S.email.ssl}>
              {(id) => (
                <TextField id={id} value={form.emailSslOptions} onChange={set('emailSslOptions')} />
              )}
            </Field>
            <Field label={S.email.serviceState}>
              {(id) => (
                <Select
                  id={id}
                  value={form.emailServiceState}
                  onChange={set('emailServiceState')}
                  options={YES_NO}
                />
              )}
            </Field>
            {/* Sent with the values in the form, not the stored ones — see runEmailTest. */}
            <ToggleRow
              id="chkBxSkipCertCheck"
              checked={String(form.disableCertValidation) === 'true'}
              onChange={(checked) => set('disableCertValidation')(String(checked))}
              label={S.email.skipCertCheck}
              hint={S.email.testHint}
            />
          </SettingsCard>

          {/* Where the probe lands — asked for, as the legacy screen asks. */}
          <PromptDialog
            open={askingTest}
            icon={Mails}
            title={S.email.testTitle}
            subtitle={S.email.title}
            label={S.email.testAddress}
            hint={S.email.testAddressHint}
            placeholder="name@example.com"
            type="email"
            dir="ltr"
            confirmLabel={S.email.testSend}
            busy={testing}
            onSubmit={runEmailTest}
            onClose={() => setAskingTest(false)}
          />
        </div>
      )}

      {tab === 'messaging' && (
        <div className="set-grid">
          {CHANNELS.map((channel) => (
            <SettingsCard
              key={channel.id}
              title={S.messaging[channel.id]}
              onSave={save(channel.id)}
              onReset={reset(channel.id)}
              saving={saving === channel.id}
            >
              <ToggleRow
                id={`chk-${channel.id}`}
                checked={!!form[channel.enabledKey]}
                onChange={toggle(channel.enabledKey)}
                label={S.messaging.enabled}
              />
              {channel.fields.map((field) => (
                <Field key={field.key} label={S.messaging[field.label]}>
                  {(id) => <TextField id={id} value={form[field.key]} onChange={set(field.key)} />}
                </Field>
              ))}
            </SettingsCard>
          ))}
        </div>
      )}

      {tab === 'notifications' && (
        /* createDataTable('templatesDT', …, false) — this table has paging off. */
        <DataTable
          columns={templateColumns}
          rows={data.notificationTemplates}
          paginate={false}
          initialSort={{ key: 'index', dir: 'asc' }}
          getRowId={(row) => row.id}
          /* No toolbar: that button had no handler either. */
        />
      )}
        </div>
      </div>
    </>
  )
}

/** Maps the stored settings onto the form the view binds to. */
function initialForm(data) {
  const s = data.settings
  return {
    ...sectionDefaults('logo', data),
    ...sectionDefaults('loginTheme', data),
    ...sectionDefaults('appTitle', data),
    ...sectionDefaults('footer', data),
    ...sectionDefaults('organization', data),
    ...sectionDefaults('domain', data),
    ...sectionDefaults('contact', data),
    ...sectionDefaults('video', data),
    ...sectionDefaults('userManual', data),
    ...sectionDefaults('adminGuide', data),
    ...sectionDefaults('reports', data),
    ...sectionDefaults('email', data),
    ...sectionDefaults('servicesUrl', data),
    ...sectionDefaults('whatsapp', data),
    ...sectionDefaults('slack', data),
    ...sectionDefaults('sms', data),
    logo: null,
    reportLogo: null,
    sideLoginAr: null,
    sideLoginEn: null,
    backgroundAr: null,
    backgroundEn: null,
    favicon: null,
    guideAr: null,
    guideEn: null,
    introVideo: pick(s, 'EnableIntroVideo') === 'true',
  }
}

/** The values a card's إلغاء button restores — what the server rendered into the form. */
function sectionDefaults(section, data) {
  const s = data?.settings ?? {}
  const company = data?.company ?? {}
  const columns = (key) => (pick(s, key) ? pick(s, key).split(',') : [])

  switch (section) {
    case 'logo':
      return { logo: null, logoBackground: pick(s, 'LogoBackgroundEnabled', 'true') }
    case 'reportLogo':
      return { reportLogo: null }
    case 'sideLogin':
      return { sideLoginAr: null, sideLoginEn: null }
    case 'backgroundLogin':
      return { backgroundAr: null, backgroundEn: null }
    case 'favicon':
      return { favicon: null }
    case 'loginTheme':
      return {
        theme: pick(s, 'LoginPageTheme', 'white'),
        logoOnLogin: pick(s, 'LogoOnLoginPage', 'false'),
      }
    case 'appTitle':
      return {
        arabicAppTitle: pick(s, 'Default_ArabicApplicationTitle'),
        englishAppTitle: pick(s, 'Default_EnglishApplicationTitle'),
      }
    case 'footer':
      return { footer: pick(s, 'WebsiteFooterText'), footerEn: pick(s, 'WebsiteFooterTextEn') }
    case 'organization':
      return {
        arabicOrgName: company.arabicName ?? '',
        englishOrgName: company.englishName ?? '',
        orgEmail: company.email ?? '',
      }
    case 'domain':
      return { domainUrl: pick(s, 'DomainURL'), useHsts: pick(s, 'UseHsts', 'false') }
    case 'contact':
      return {
        enableContactPage: pick(s, 'ShowSupportContactInfoPage') === 'true',
        contactPageTitle: pick(s, 'ContactPageTitle'),
        contactPageTitleEn: pick(s, 'ContactPageTitleEn'),
        contactPageHeader: pick(s, 'ContactPageHeader'),
        contactPageHeaderEn: pick(s, 'ContactPageheaderEn'),
        enableEmail: pick(s, 'ContactEmailShowSendForm') === 'true' || !!pick(s, 'ContactEmail'),
        contactEmail: pick(s, 'ContactEmail'),
        showSendForm: pick(s, 'ContactEmailShowSendForm') === 'true',
        showEmailInfo: pick(s, 'ContactEmailShowEmailInfo') === 'true',
        contactEmailLabel: pick(s, 'ContactEmailLabelText'),
        contactEmailEnLabel: pick(s, 'ContactEmailEnLabelText'),
        showPhone: pick(s, 'ContactEmailShowPhoneInfo') === 'true',
        contactPhone: pick(s, 'ContactPhone'),
        contactPhoneLabel: pick(s, 'ContactPhoneLabelText'),
        contactPhoneEnLabel: pick(s, 'ContactPhoneEnLabelText'),
      }
    case 'video':
      return {
        progressBar: pick(s, 'Default_EnablePlayerProgressbar') === 'true',
        forceSubtitle: pick(s, 'Default_EnableForceSubtitle') === 'true',
        userSubtitleControl: pick(s, 'EnableUserSubtitleControl') === 'true',
        pauseOnBlur: pick(s, 'Default_EnablePlayerBlur') === 'true',
        playbackRates: pick(s, 'Default_EnablePlayerPlaybackRates') === 'true',
      }
    case 'userGuide':
      return { guideAr: null, guideEn: null }
    case 'userManual':
      return {
        manualPopup: pick(s, 'Default_EnableUserManualPopup') === 'true',
        introVideo: pick(s, 'EnableIntroVideo') === 'true',
        guideLinkOnLogin: pick(s, 'UserGuideLinkOnTheLoginPage') === 'true',
        guideSeparatePage: pick(s, 'ShowUserGuideOnSeparatePage') === 'true',
      }
    case 'adminGuide':
      return { adminManualUrl: pick(s, 'AdminPanelUserManualUrl') }
    case 'email':
      return {
        emailHost: pick(s, 'Email_Host'),
        emailPort: pick(s, 'Email_Port'),
        emailFrom: pick(s, 'Email_From'),
        emailSenderName: pick(s, 'Email_SenderName'),
        emailUsername: pick(s, 'Email_Username'),
        emailPassword: pick(s, 'Email_Password'),
        emailSslOptions: pick(s, 'Email_SslOptions'),
        emailServiceState: pick(s, 'Email_ServiceState', 'false'),
        disableCertValidation: pick(s, 'DisableMailServerCertificateValidation', 'false'),
      }
    case 'servicesUrl':
      return { servicesUrl: pick(s, 'ServicesUrl') }
    case 'whatsapp': {
      const c = parseChannel(pick(s, 'WhatsAppSettings', '{}'))
      return {
        whatsappEnabled: c.Enabled === true,
        whatsappApiUrl: c.ApiUrl ?? '',
        whatsappApiKey: c.ApiKey ?? '',
        whatsappSender: c.SenderNumber ?? '',
      }
    }
    case 'slack': {
      const c = parseChannel(pick(s, 'SlackSettings', '{}'))
      return {
        slackEnabled: c.Enabled === true,
        slackWebhook: c.WebhookUrl ?? '',
        slackBotToken: c.BotToken ?? '',
        slackChannel: c.Channel ?? '',
      }
    }
    case 'sms': {
      const c = parseChannel(pick(s, 'SmsSettings', '{}'))
      return {
        smsEnabled: c.Enabled === true,
        smsApiUrl: c.ApiUrl ?? '',
        smsApiKey: c.ApiKey ?? '',
        smsSenderId: c.SenderId ?? '',
      }
    }
    case 'reports':
      return {
        userStatistics: pick(s, 'User_Statistics_IsEnabled').toLowerCase() === 'true',
        policiesColumns: columns('Reports_Policies_Profile_Columns'),
        examsColumns: columns('Reports_Exams_Profile_Columns'),
      }
    default:
      return {}
  }
}
